import Button from "components/Button";
import { useMemo, useState } from "react";

import { IcoAccessibilityQuestionCircle } from "assets/icons";
import DocumentFolderInput from "components/DocumentFolderInput";
import useUserToken from "hooks/useUserToken";
import { FeatureFlag } from "models/auth/types";
import { Tooltip } from "react-tooltip";
import { ApplicationFormDocumentFolder, DocumentFolderType, DocumentFolderTypesAndFileExtensions } from "services/types";
import * as Yup from "yup";
import { Section, SectionErrorMessage, StepLoading } from "../layout";
import { ApplicationDocumentValidationStatus, ApplicationFormik } from "../types";
import DocumentTemplateModal from "./DocumentTemplateModal";

export const applicationDocumentsSchema = Yup.array().of(
	Yup.object().shape({
		name: Yup.string().nullable().required(),
		blobName: Yup.string().nullable().required()
	})
);

export const applicationDocumentFolderSchema = Yup.object().shape({
	documents: applicationDocumentsSchema,
	exemptionDocumentFolderType: Yup.string().required(),
	id: Yup.string().optional()
});

export default function ApplicationDocumentsStep({
	formik,
	documentFolderTypesData,
	isLoadingFolderTypes,
	validatedDocuments,
	setValidatedDocuments
}: Readonly<ApplicationFormik> & {
	documentFolderTypesData: DocumentFolderTypesAndFileExtensions | null;
	isLoadingFolderTypes: boolean;
	validatedDocuments: { [folderTypeId: string]: string[] };
	setValidatedDocuments: (newDocumentsValidated: { [folderTypeId: string]: string[] }) => void;
}): JSX.Element {
	const tokenInfo = useUserToken();
	const { values, setFieldValue, setFieldTouched, errors, touched } = formik;
	const foldersErrorMessage = typeof errors.documentFolders === "string" ? errors.documentFolders : null;
	const { documentFolders } = values;
	const setFolderDocumentsValue = (newFolderValue: ApplicationFormDocumentFolder) => {
		const fieldKey = `documentFolders.${newFolderValue.exemptionDocumentFolderType}.documents`;
		setFieldTouched(fieldKey, true);
		return setFieldValue(fieldKey, newFolderValue.documents);
	};

	const [isDocumentModalOpen, setIsDocumentModalOpen] = useState<boolean>(false);
	const requiredDocuments = useMemo(
		() => documentFolderTypesData?.documentFolderTypes.filter((folderType) => folderType.alwaysRequired),
		[documentFolderTypesData]
	);
	const optionalDocuments = useMemo(
		() => documentFolderTypesData?.documentFolderTypes.filter((folderType) => !folderType.alwaysRequired),
		[documentFolderTypesData]
	);
	const optionalDocumentsDetails = useMemo(
		() =>
			optionalDocuments?.reduce((acc: { [details: string]: DocumentFolderType[] }, obj) => {
				const { details } = obj.documentType;
				if (!(details in acc)) {
					acc[details] = [];
				}
				acc[details].push(obj);
				return acc;
			}, {}),
		[optionalDocuments]
	);

	const anchorHelpTooltip = "optional-documents-help-tooltip";
	const validationEnabled = useMemo(() => {
		return (
			tokenInfo?.hasFeatureEnabled(FeatureFlag.VALIDATE_VEHICLE_DOCUMENT) &&
			formik.values.applicationVehicle.documentValidationStatus !== ApplicationDocumentValidationStatus.API_UNAVAILABLE
		);
	}, [formik.values.applicationVehicle.documentValidationStatus, tokenInfo]);

	return (
		<div className="flex flex-col overflow-y-auto p-8 h-full max-w-3xl">
			<div className="text-sm text-neutral-low-400">Anexe os documentos solicitados em seus respectivos campos</div>
			{isLoadingFolderTypes ? (
				<StepLoading />
			) : (
				<>
					{touched.documentFolders && foldersErrorMessage && <SectionErrorMessage error={foldersErrorMessage} />}
					<Section>
						<div className="my-4 flex flex-col gap-4">
							<Button hierarchy="ghost" kind="link" size="small" className="w-fit px-0" onClick={() => setIsDocumentModalOpen(true)}>
								Consulte os documentos obrigatórios
							</Button>

							<a
								href="https://portalservicos.senatran.serpro.gov.br/static/carteiradigital/tutoriais/html/demo_13.html"
								target="blank"
								className="text-sm underline"
							>
								Como baixar meu Documento do Veículo (CRLV - Certificado de Registro e Licenciamento de Veículo)
							</a>
						</div>
						{!!Object.keys(documentFolders).length && (
							<div className="flex flex-col pt-2 gap-8 w-full">
								<div className="flex flex-col gap-8 w-full">
									{requiredDocuments?.map((folderType) => {
										return (
											<DocumentFolderInput
												key={folderType.id}
												folderName={folderType.documentType.name}
												expectedFormats={folderType.expectedFormatsText}
												folderType={folderType.id}
												minDocumentCount={folderType.minAmountDocuments}
												value={documentFolders[folderType.id]}
												setValue={setFolderDocumentsValue}
												errors={formik.errors.documentFolders?.[folderType.id]}
												touched={formik.touched.documentFolders?.[folderType.id]}
												fileExtensions={documentFolderTypesData?.fileExtensions ?? null}
												multiple={!folderType.documentType.isValidatedByApi}
												validatedDocuments={validatedDocuments[folderType.id]}
												setValidatedDocuments={(newValidatedDocuments) =>
													setValidatedDocuments({ ...validatedDocuments, [folderType.id]: newValidatedDocuments })
												}
												shouldValidate={validationEnabled && folderType.documentType.isValidatedByApi}
											/>
										);
									})}
								</div>
								{optionalDocuments && optionalDocuments.length > 0 && (
									<div className="flex flex-col gap-6 w-full">
										<div className="flex flex-row items-center w-full gap-1">
											<h3 className="text-base"> Documentos complementares </h3>
											<Tooltip anchorSelect={`#${anchorHelpTooltip}`} place="right" className="max-w-[330px]">
												Documentos complementares podem ser obrigatórios, dependendo do tipo de solicitação. Verifique os
												documentos.
											</Tooltip>
											<div className="cursor-pointer" id={anchorHelpTooltip}>
												<IcoAccessibilityQuestionCircle />
											</div>
										</div>
										<div className="flex flex-col gap-8 w-full">
											{optionalDocumentsDetails &&
												Object.entries(optionalDocumentsDetails).map(([details, folderTypes]) => {
													return (
														<div key={`key-${details}`}>
															<div className="flex w-full text-neutral-low-400 text-sm mb-6">
																{details === "null" ? "Outros" : details}
															</div>
															<div className="flex flex-col gap-8 w-full">
																{folderTypes.map((folderType) => {
																	return (
																		<DocumentFolderInput
																			key={folderType.id}
																			folderName={folderType.documentType.name}
																			expectedFormats={folderType.expectedFormatsText}
																			folderType={folderType.id}
																			minDocumentCount={folderType.minAmountDocuments}
																			value={documentFolders[folderType.id]}
																			setValue={setFolderDocumentsValue}
																			errors={formik.errors.documentFolders?.[folderType.id]}
																			touched={formik.touched.documentFolders?.[folderType.id]}
																			fileExtensions={documentFolderTypesData?.fileExtensions ?? null}
																			multiple={!folderType.documentType.isValidatedByApi}
																			validatedDocuments={validatedDocuments[folderType.id]}
																			setValidatedDocuments={(newValidatedDocuments) =>
																				setValidatedDocuments({
																					...validatedDocuments,
																					[folderType.id]: newValidatedDocuments
																				})
																			}
																			shouldValidate={
																				validationEnabled && folderType.documentType.isValidatedByApi
																			}
																		/>
																	);
																})}
															</div>
														</div>
													);
												})}
										</div>
									</div>
								)}
							</div>
						)}
						{requiredDocuments && (
							<DocumentTemplateModal
								isOpen={isDocumentModalOpen}
								setIsOpen={setIsDocumentModalOpen}
								requiredDocuments={requiredDocuments}
								optionalDocuments={optionalDocuments}
								sectionTitle={formik.values.exemptionType?.name ?? ""}
							/>
						)}
					</Section>
					{touched.documentFolders && foldersErrorMessage && <SectionErrorMessage error={foldersErrorMessage} />}
				</>
			)}
		</div>
	);
}
